import { camelCase, upperFirst } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button, Searchbar, Table, Theme } from '../../../components';
import { FetchCrudData, GetTableDataOptions, SORT_ORDER, TableColumn, User, UserField } from '../../../models';
import { usersActions, usersSelectors } from '../../../redux';
import { ITEMS_PER_PAGE } from '../../constants';
import AddEditUser from './../components/AddEditUser';
import DeleteUser from './../components/DeleteUser';

import '../admin.scss';

const USER_COLUMNS: TableColumn[] = [
  {
    label: 'lblEmail',
    field: UserField.EMAIL,
  },
  {
    label: 'lblRole',
    field: UserField.ROLES,
  },
  {
    label: 'lblStore',
    field: UserField.STORE_NAME,
  },
  {
    label: 'lblFirstName',
    field: UserField.FIRST_NAME,
  },
  {
    label: 'lblLastName',
    field: UserField.LAST_NAME,
  },
  {
    label: '',
    field: 'actions',
    sortable: false,
  },
];

export type UserModal = 'ADD_EDIT_USER' | 'DELETE_USER';

const UserManagement = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const users = useSelector(usersSelectors.getUsers);
  const isLoading = useSelector(usersSelectors.isLoading);
  const total = useSelector(usersSelectors.getTotal);

  const [filters, setFilters] = useState<FetchCrudData>({
    offset: 0,
    limit: ITEMS_PER_PAGE,
    sort: [UserField.EMAIL + ',' + SORT_ORDER.ASC],
  });
  const [activeUser, setActiveUser] = useState<User>(null);
  const [openedModal, setOpenedModal] = useState<UserModal | null>(null);
  const [searchTerm, setSearchTerm] = useState<string | ''>('');

  useEffect(() => {
    if (filters.offset === 0) {
      dispatch(usersActions.getUsers(filters));
    } else if (filters.offset < total) {
      dispatch(usersActions.getMoreUsers(filters));
    }
  }, [filters]);

  const renderTableCell = (value: string, field: string, user: unknown) => {
    switch (field) {
      case 'roles':
        const role = ((user as { roles: string[] }).roles || [])[0];
        if (role) {
          return t('lbl' + upperFirst(camelCase(role))).toLowerCase();
        }
        return '-';

      case 'store.name':
        return (user as { store: { name: string } })?.store?.name || '-';

      case 'actions':
        return (
          <>
            <Button theme={Theme.TRANSPARANT} icon='Edit' onClickCallback={() => editUser(user)} />
            <Button theme={Theme.TRANSPARANT} icon='Trashcan' onClickCallback={() => deleteUser(user)} />
          </>
        );

      default:
        return value || '-';
    }
  };

  const editUser = user => {
    setActiveUser(user);
    setOpenedModal('ADD_EDIT_USER');
  };

  const addUser = () => {
    setOpenedModal('ADD_EDIT_USER');
  };

  const deleteUser = user => {
    setActiveUser(user);
    setOpenedModal('DELETE_USER');
  };

  const getData = (options: GetTableDataOptions) => {
    setFilters((filters): FetchCrudData => {
      return {
        ...filters,
        offset: filters.limit * options.page,
        sort: [options.sortColumn + ',' + options.sortOrder],
      };
    });
  };

  const closeModals = () => {
    setActiveUser(null);
    setOpenedModal(null);
    setFilters((filters): FetchCrudData => {
      return {
        ...filters,
        offset: 0,
      };
    });
  };

  const executeSearch = () => {
    if (!searchTerm) {
      setFilters(oldFilters => {
        return {
          ...oldFilters,
          offset: 0,
          s: undefined,
        };
      });
    }
    setFilters(oldFilters => {
      return {
        ...oldFilters,
        offset: 0,
        s: {
          $or: [
            {
              firstName: {
                $contL: searchTerm,
              },
            },
            {
              lastName: {
                $contL: searchTerm,
              },
            },
            {
              email: {
                $contL: searchTerm,
              },
            },
          ],
        },
      };
    });
  };

  return (
    <div className='content'>
      <div className='sticky-header'>
        <div className='top-buttons'>
          <Searchbar
            placeholder={t('lblSearchUser')}
            isLoading={isLoading}
            value={searchTerm}
            onChange={setSearchTerm}
            onEnter={executeSearch}
          />
          <Button theme={Theme.SECONDARY} onClickCallback={addUser} icon='Add'>
            {t('lblAddUser')}
          </Button>
        </div>
        <h1 className='title'>{t('titleUsers')}</h1>
      </div>
      <section>
        <Table
          columns={USER_COLUMNS}
          renderTableCell={renderTableCell}
          data={users}
          getData={getData}
          page={Math.floor(filters.offset / ITEMS_PER_PAGE)}
          isLoading={isLoading}
          defaultSortColumn={'email'}
          defaultSortOrder={SORT_ORDER.ASC}
        />
      </section>
      <AddEditUser closeModal={closeModals} user={activeUser} isOpen={openedModal === 'ADD_EDIT_USER'} />
      <DeleteUser closeModal={closeModals} deleteId={activeUser?.id} isOpen={openedModal === 'DELETE_USER'} />
    </div>
  );
};

export default UserManagement;
